import * as THREE from 'three';
import { materialPool } from './MaterialPool';
import type { CollisionSystem } from './CollisionSystem';

// ============================================================
// Missile lock-on and tracking system
// ============================================================

const MAX_MISSILES = 4;
const RELOAD_TIME = 5;         // seconds per missile
const FIRE_COOLDOWN = 2;       // minimum seconds between launches
const LOCK_TIME = 1.5;         // seconds to acquire lock
const MISSILE_SPEED = 300;
const MISSILE_TURN_RATE = 2.0; // radians/sec — slow enough to dodge
const MISSILE_LIFETIME = 7;    // seconds before self-destruct
const MISSILE_LENGTH = 4;
const MISSILE_RADIUS = 0.4;
const LOCK_SCREEN_RADIUS = 0.05; // target must be within this fraction of screen center

// Shared geometry
const missileGeometry = new THREE.CylinderGeometry(MISSILE_RADIUS, MISSILE_RADIUS * 0.3, MISSILE_LENGTH, 6, 1);
missileGeometry.rotateX(Math.PI / 2);

interface ActiveMissile {
  readonly mesh: THREE.Mesh;
  readonly target: THREE.Object3D;
  readonly direction: THREE.Vector3;
  age: number;
}

// Reusable vectors
const _toTarget = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _screenPos = new THREE.Vector3();
const _lookTarget = new THREE.Vector3();

export class MissileSystem {
  private readonly scene: THREE.Scene;
  private readonly missiles: ActiveMissile[] = [];
  private ammo = MAX_MISSILES;
  private reloadTimer = 0;
  private fireCooldown = 0;
  private lockTimer = 0;
  private lockedTarget: THREE.Object3D | null = null;
  private locking = false;
  private readonly targets: THREE.Object3D[] = [];

  private collisionSystem: CollisionSystem | null = null;

  /** Called when a missile is launched — (target mesh, start position, direction) */
  onLaunch: ((target: THREE.Object3D, position: THREE.Vector3, direction: THREE.Vector3) => void) | null = null;

  /** Called when a missile is removed (hit, expired, or building collision) */
  onMissileRemoved: ((position: THREE.Vector3) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  setCollisionSystem(cs: CollisionSystem): void {
    this.collisionSystem = cs;
  }

  addTarget(target: THREE.Object3D): void {
    this.targets.push(target);
  }

  removeTarget(target: THREE.Object3D): void {
    const idx = this.targets.indexOf(target);
    if (idx >= 0) this.targets.splice(idx, 1);
  }

  update(
    aircraft: THREE.Object3D,
    camera: THREE.PerspectiveCamera,
    lockMode: boolean,
    firePressed: boolean,
    dt: number,
    missileHitCheck?: (pos: THREE.Vector3, target: THREE.Object3D) => boolean,
    onExpire?: (pos: THREE.Vector3) => void,
  ): void {
    // ── Reload ────────────────────────────────────────────
    if (this.ammo < MAX_MISSILES) {
      this.reloadTimer += dt;
      if (this.reloadTimer >= RELOAD_TIME) {
        this.reloadTimer = 0;
        this.ammo = Math.min(MAX_MISSILES, this.ammo + 1);
      }
    }

    if (this.fireCooldown > 0) this.fireCooldown -= dt;

    // ── Lock-on ──────────────────────────────────────────
    if (lockMode) {
      let candidate = this.findLockCandidate(aircraft, camera);
      // Break lock if building blocks line of sight
      if (candidate && this.collisionSystem &&
          !this.collisionSystem.hasLineOfSight(aircraft.position, candidate.position)) {
        candidate = null;
      }
      if (candidate) {
        if (candidate === this.lockedTarget) {
          this.lockTimer += dt;
        } else {
          this.lockedTarget = candidate;
          this.lockTimer = 0;
        }
        this.locking = true;
      } else {
        this.lockedTarget = null;
        this.lockTimer = 0;
        this.locking = false;
      }

      // Fire missile
      if (
        firePressed &&
        this.lockedTarget &&
        this.lockTimer >= LOCK_TIME &&
        this.ammo > 0 &&
        this.fireCooldown <= 0
      ) {
        this.launchMissile(aircraft, this.lockedTarget);
      }
    } else {
      this.lockedTarget = null;
      this.lockTimer = 0;
      this.locking = false;
    }

    // ── Update active missiles ──────────────────────────
    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const m = this.missiles[i];
      m.age += dt;

      if (m.age >= MISSILE_LIFETIME) {
        if (onExpire) onExpire(m.mesh.position.clone());
        this.scene.remove(m.mesh);
        materialPool.release(m.mesh.material as THREE.MeshBasicMaterial);
        this.missiles.splice(i, 1);
        continue;
      }

      // Steer toward target (skip if dumb missile — target was flared)
      if (m.target) {
        _toTarget.copy(m.target.position).sub(m.mesh.position).normalize();
        const maxTurn = MISSILE_TURN_RATE * dt;
        m.direction.lerp(_toTarget, Math.min(maxTurn, 1));
        m.direction.normalize();
      }

      // Move
      m.mesh.position.addScaledVector(m.direction, MISSILE_SPEED * dt);

      // Building collision — missile explodes on impact
      if (this.collisionSystem &&
          this.collisionSystem.isBlockedByBuilding(m.mesh.position, m.direction, MISSILE_SPEED * dt)) {
        if (onExpire) onExpire(m.mesh.position.clone());
        this.scene.remove(m.mesh);
        materialPool.release(m.mesh.material as THREE.MeshBasicMaterial);
        this.missiles.splice(i, 1);
        continue;
      }

      // Orient mesh
      _lookTarget.copy(m.mesh.position).add(m.direction);
      m.mesh.lookAt(_lookTarget);

      // Hit detection
      if (m.target && missileHitCheck && missileHitCheck(m.mesh.position, m.target)) {
        this.scene.remove(m.mesh);
        materialPool.release(m.mesh.material as THREE.MeshBasicMaterial);
        this.missiles.splice(i, 1);
        continue;
      }
    }
  }

  private findLockCandidate(
    _aircraft: THREE.Object3D,
    camera: THREE.PerspectiveCamera,
  ): THREE.Object3D | null {
    let bestTarget: THREE.Object3D | null = null;
    let bestDist = LOCK_SCREEN_RADIUS;

    for (const target of this.targets) {
      // Project target onto screen (-1 to 1 in NDC)
      _screenPos.copy(target.position).project(camera);

      // Must be in front of camera (z < 1 in NDC)
      if (_screenPos.z > 1) continue;

      // Distance from screen center
      const dist = Math.sqrt(_screenPos.x * _screenPos.x + _screenPos.y * _screenPos.y);

      // Must be inside crosshair area
      if (dist < bestDist) {
        bestDist = dist;
        bestTarget = target;
      }
    }

    return bestTarget;
  }

  private launchMissile(aircraft: THREE.Object3D, target: THREE.Object3D): void {
    this.ammo--;
    this.fireCooldown = FIRE_COOLDOWN;

    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    const start = aircraft.position.clone().addScaledVector(_forward, 10);

    const mat = materialPool.acquire();
    mat.color.set(0xff4444);
    const mesh = new THREE.Mesh(missileGeometry, mat);
    mesh.position.copy(start);
    mesh.frustumCulled = false;

    // Add glow
    const glow = new THREE.PointLight(0xff2200, 3, 100);
    mesh.add(glow);

    this.scene.add(mesh);

    const dir = _forward.clone();
    this.missiles.push({
      mesh,
      target,
      direction: dir,
      age: 0,
    });

    this.onLaunch?.(target, start.clone(), dir.clone());
  }

  get isLocking(): boolean {
    return this.locking;
  }

  get isLocked(): boolean {
    return this.lockTimer >= LOCK_TIME && this.lockedTarget !== null;
  }

  get lockProgress(): number {
    return Math.min(1, this.lockTimer / LOCK_TIME);
  }

  get currentAmmo(): number {
    return this.ammo;
  }

  get maxAmmo(): number {
    return MAX_MISSILES;
  }

  get lockedTargetObject(): THREE.Object3D | null {
    return this.lockedTarget;
  }

  /** How many active missiles are tracking a given target */
  getMissilesTargeting(target: THREE.Object3D): number {
    return this.missiles.filter((m) => m.target === target).length;
  }

  /** Is this target being locked on (but not yet fired at) */
  isBeingLockedOn(target: THREE.Object3D): boolean {
    return this.locking && this.lockedTarget === target;
  }

  /** Break all missile locks on a target — missiles go dumb (fly straight) */
  breakLocksOn(target: THREE.Object3D): void {
    for (const m of this.missiles) {
      if (m.target === target) {
        // Missile keeps flying in current direction but stops tracking
        (m as { target: THREE.Object3D }).target = null as unknown as THREE.Object3D;
      }
    }
    // Also break active lock-on
    if (this.lockedTarget === target) {
      this.lockedTarget = null;
      this.lockTimer = 0;
      this.locking = false;
    }
  }

  dispose(): void {
    for (const m of this.missiles) {
      this.scene.remove(m.mesh);
      materialPool.release(m.mesh.material as THREE.MeshBasicMaterial);
    }
    this.missiles.length = 0;
  }
}
